const { withPrefix } = require('gatsby');
const { urlsNotToRedirect } = require('./redirectBlackList');

const i18nEnabled = () => JSON.parse(process.env.GATSBY_LANGUAGES).length > 0;

const getPathWithoutPrefix = (pathname) => {
  const prefix = withPrefix('/');
  return pathname.replace(RegExp(`^${prefix}`), '');
};

const getI18nPrefix = (pathname) => {
  const pathNoPrefix = getPathWithoutPrefix(pathname);
  // Assumes language code is a 2-letter lowercase ascii prefix...
  // ...and any such prefix is a language code

  // matches 'es/<stuff>' or '/es'
  const regexPattern = '^([a-z]{2})(/|$)';
  const langMatch = pathNoPrefix.match(RegExp(regexPattern));
  return langMatch && langMatch[1]; // HACK: prod version of node doesn't support named groups
};

const getBrowserLanguage = () => navigator.language.substring(0, 2);

const getI18nPathname = (pathname, languageCode) => {
  const pathNoPrefix = getPathWithoutPrefix(pathname);
  const langPrefix = getI18nPrefix(pathNoPrefix);

  const basePathname = !langPrefix
    ? pathNoPrefix
    : pathNoPrefix.replace(RegExp(`^${langPrefix}(/|$)`), '');

  const returnPath = languageCode === 'en' ? `/${basePathname}` : `/${languageCode}/${basePathname}`;

  // remove trailing slash if that's not the whole path
  return returnPath === '/' ? returnPath : returnPath.replace(/\/$/, '');
};

const withI18nPrefix = (pathname, languageCode) => {
  const languageSpecificPathname = getI18nPathname(pathname, languageCode);
  return withPrefix(languageSpecificPathname);
};

const redirectToTranslatedPage = ({ pathname, search: queryParams }, languageCode) => {
  window.location.href = `${withI18nPrefix(pathname, languageCode)}${queryParams}`;
};

// Function to check if a URL is allowed
function shouldRedirect(location) {
  const pathsNotToRedirect = urlsNotToRedirect;

  // Check if the path is in the allowed paths list
  if (pathsNotToRedirect?.includes(location?.pathname)) {
    return false;
  }

  // Check if the path matches any patterns
  let notMatchingPattern = true;
  pathsNotToRedirect.every((pattern) => {
    if (pattern.endsWith('/*') && location.pathname.startsWith(pattern.slice(0, -2))) {
      notMatchingPattern = false;
      return false;
    }
    return true;
  });

  // URL is allowed
  return notMatchingPattern;
}

const redirectToUserLanguage = (location, locale, userLanguage) => {
  if (userLanguage && userLanguage !== locale && !getI18nPrefix(location.pathname) && shouldRedirect(location)) {
    redirectToTranslatedPage(location, userLanguage);
  }
};

const defaultUserCurrency = { code: 'USD', rate: 1.0, symbol: '$' };

const getReverseLangCode = (lang) => (
  lang === 'es' ? 'en' : 'es'
);

const removeTrailingSlash = (url) => (
  url.endsWith('/')
    ? url.substr(0, url.length - 1)
    : url
);

const getUrlsFromPath = ({
  path, lang,
} = {}) => {
  if (typeof path !== 'string') {
    throw new TypeError(
      "getUrlsFromPath() Expected parameters object to contain 'path' of type"
      + `"string" ex. '{ path: "/masters" }' but "${typeof path}" is given.`,
    );
  }

  const normalizedPath = path.toLowerCase().trim();
  const pathWithoutSlash = normalizedPath.startsWith('/')
    ? normalizedPath.substring(1)
    : normalizedPath;
  const isRootEsPath = pathWithoutSlash === 'es';
  const languageFromPath = (pathWithoutSlash.startsWith('es/') || isRootEsPath)
    ? 'es'
    : 'en';
  const detectedLang = lang || languageFromPath;
  const pathWithoutLangPrefix = pathWithoutSlash.startsWith('es/')
    ? pathWithoutSlash.substring(3)
    : pathWithoutSlash;

  const pathWithoutLang = isRootEsPath
    ? ''
    : pathWithoutLangPrefix;
  const pathWithoutTrailingSlash = removeTrailingSlash(pathWithoutLang);
  const pathWithPrecedingSlash = pathWithoutTrailingSlash ? `/${pathWithoutTrailingSlash}` : '';
  const url = detectedLang === 'en'
    ? `${process.env.GATSBY_SITE_DOMAIN}${pathWithPrecedingSlash}`
    : `${process.env.GATSBY_SITE_DOMAIN}/es${pathWithPrecedingSlash}`;
  const otherUrl = detectedLang === 'en'
    ? `${process.env.GATSBY_SITE_DOMAIN}/es${pathWithPrecedingSlash}`
    : `${process.env.GATSBY_SITE_DOMAIN}${pathWithPrecedingSlash}`;
  return {
    url,
    lang: detectedLang,
    otherUrl,
    otherLang: getReverseLangCode(detectedLang),
  };
};

module.exports = {
  getI18nPrefix,
  getBrowserLanguage,
  getI18nPathname,
  redirectToTranslatedPage,
  redirectToUserLanguage,
  withI18nPrefix,
  i18nEnabled,
  defaultUserCurrency,
  getUrlsFromPath,
};
